import { call, put, select, delay } from 'redux-saga/effects'
import { Saga } from 'app-types'

import { contractActions, contractSelectors } from '../../redux'
import { ContractSignStatus, PreContractStatus } from '../../constants/enums'
import { OpenApiInterface } from '../../api/requests/openApi'
import { POLLING_DELAY_IN_MS } from '../../constants/commonConstants'
import { Response } from '../../api/api'
import { ResponseError } from '../../utils/errors'
import { Action } from '../../types'
import { Err, ErrSuccess } from '../../types/form'
import { mapAdobeCookie } from './contract.mappers'

export function* signContract(api: OpenApiInterface, action: any): Saga {
  const status = yield select(contractSelectors.getContractSignStatus)

  switch (status) {
    case ContractSignStatus.OK:
      yield call(action.payload.success)
      break
    case ContractSignStatus.IN_PROGRESS:
      yield delay(POLLING_DELAY_IN_MS)
      const wasOk = yield call(fetchContractSigningStatus, api, action)

      if (wasOk) {
        yield put(contractActions.signContract(action.payload))
      }
      break
    default:
      throw new ResponseError(`contract saga - signContract wrong status ${status}`)
  }
}

export function* fetchContractSigningStatus(
  api: OpenApiInterface,
  { payload: { error } }: Action<ErrSuccess>
): Generator<any, boolean, any> {
  const response = yield call(api.getContractSignStatus)

  if (response.responseStatus === Response.ok) {
    const data = response.data
    yield put(contractActions.saveContractSignStatus(data.status))

    return true
  }
  error(response.responseStatus)

  return false
}

export function* startPrintPreContractSaga(
  api: OpenApiInterface,
  { payload }: Action<ErrSuccess>
): Saga {
  yield call(api.postPreContractGenerate)
  yield put(contractActions.savePreContractStatus(PreContractStatus.IN_APPROVAL))

  yield put(contractActions.printPreContract(payload))
}

export function* printPreContractSaga(api: OpenApiInterface, action: Action<ErrSuccess>): Saga {
  const status = yield select(contractSelectors.getPreContractStatus)
  switch (status) {
    case PreContractStatus.OK:
      yield action.payload.success()
      break
    case PreContractStatus.IN_APPROVAL:
      yield delay(POLLING_DELAY_IN_MS)
      const wasOk = yield call(fetchPreContractStatus, api, action)

      if (wasOk) {
        yield put(contractActions.printPreContract(action.payload))
      }
      break

    case PreContractStatus.RETRY:
      action.payload.error(Response.serverErrorRetry)
      break
    default:
      throw new ResponseError(`contract saga - printPreContractSaga wrong status ${status}`)
  }
}

export function* fetchPreContractStatus(
  api: OpenApiInterface,
  { payload: { error } }: Action<ErrSuccess>
): Generator<any, boolean, any> {
  const response = yield call(api.getPreContractStatus)

  if (response.responseStatus === Response.ok) {
    const data = response.data
    yield put(contractActions.savePreContractStatus(data.status))

    return true
  }
  error(response.responseStatus)

  return false
}

export function* fetchPreContract(
  api: OpenApiInterface,
  { payload: { error } }: Action<Err>
): Generator<any, boolean, any> {
  const response = yield call(api.getPreContract)

  if (response.responseStatus === Response.ok) {
    const data = response.data
    yield put(contractActions.savePreContract(data.binaryData))

    return true
  }
  error(response.responseStatus)

  return false
}

export function* fetchContract(
  api: OpenApiInterface,
  { payload: { error, success } }: Action<ErrSuccess>
): Saga {
  const response = yield call(api.getContract)

  if (response.responseStatus === Response.ok) {
    const data = response.data
    yield put(contractActions.saveContract(data.binaryData))
    success()
  } else {
    error(response.responseStatus)
  }
}

const fail = (response: any) => {
  throw new ResponseError('contract saga - cifSaga', response)
}

export function* createCifSaga(api: OpenApiInterface, { payload }: any): Saga {
  const data = mapAdobeCookie()

  const response = yield call(api.createCif, data)

  if (response.responseStatus !== Response.ok) {
    if (response.responseStatus === Response.serverErrorRetry) {
      payload.error({ type: response.responseStatus })
      return
    }
    fail(response)
  }
  yield delay(POLLING_DELAY_IN_MS)
  yield put(contractActions.checkCif(payload))
}

export function* checkCifSaga(api: OpenApiInterface, { payload }: any): Saga {
  const response = yield call(api.getCifStatus)

  if (response.responseStatus === Response.ok) {
    switch (response.data?.status) {
      case 'OK':
        yield call(payload.success)
        break
      case 'NOK':
        yield call(payload.deny)
        break
      case 'IN_APPROVAL':
        yield delay(POLLING_DELAY_IN_MS)
        yield put(contractActions.checkCif(payload))
        break
      default:
        fail(response)
    }
  } else if (response.responseStatus === Response.serverErrorRetry) {
    if (payload.error) {
      payload.error({ type: response.responseStatus })
    }
  } else {
    fail(response)
  }
}
